/**
 * Shared basemap catalog + picker used by every Cesium map in the app.
 *
 * One source of truth for:
 *   • Basemap IDs and human labels
 *   • Provider construction (XYZ tiles vs Cesium Ion assets)
 *   • Applying a basemap to a live Viewer (swaps the imagery layer in
 *     place, preserving terrain and entities)
 *   • A small <BasemapPicker> dropdown component
 *
 * Consumers: `components/map/CesiumMap.tsx` (live / historical / replay
 * viewer) and `components/analytics/SpatialHeatmapMap.tsx` (analytics
 * heatmap). If you add another Cesium viewer, use this module so the
 * picker behaves identically everywhere.
 */
import {
  Credit,
  IonImageryProvider,
  UrlTemplateImageryProvider,
  Viewer as CesiumViewer,
  ImageryLayer,
} from 'cesium';

export type BasemapId =
  | 'cesium-world' // Cesium Ion world imagery (default 3D satellite)
  | 'google-hybrid'
  | 'google-satellite'
  | 'google-roadmap'
  | 'osm';

interface BasemapDef {
  id: BasemapId;
  label: string;
  make: () => UrlTemplateImageryProvider | Promise<IonImageryProvider> | Promise<ImageryLayer>;
}

export const BASEMAPS: BasemapDef[] = [
  {
    id: 'cesium-world',
    label: 'Cesium World',
    // ImageryLayer.fromWorldImagery wraps Ion's default Bing imagery with
    // the extra tokens Cesium expects — use it directly so 3D terrain
    // alignment matches the original viewer setup.
    make: () => Promise.resolve(ImageryLayer.fromWorldImagery({})),
  },
  {
    id: 'google-hybrid',
    label: 'Google Hybrid',
    make: () =>
      new UrlTemplateImageryProvider({
        url: 'https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        subdomains: ['0', '1', '2', '3'],
        maximumLevel: 20,
        credit: new Credit('© Google', false),
      }),
  },
  {
    id: 'google-satellite',
    label: 'Google Satellite',
    make: () =>
      new UrlTemplateImageryProvider({
        url: 'https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        subdomains: ['0', '1', '2', '3'],
        maximumLevel: 20,
        credit: new Credit('© Google', false),
      }),
  },
  {
    id: 'google-roadmap',
    label: 'Google Roadmap',
    make: () =>
      new UrlTemplateImageryProvider({
        url: 'https://mt{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        subdomains: ['0', '1', '2', '3'],
        maximumLevel: 20,
        credit: new Credit('© Google', false),
      }),
  },
  {
    id: 'osm',
    label: 'OpenStreetMap',
    make: () =>
      new UrlTemplateImageryProvider({
        url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        maximumLevel: 19,
        credit: new Credit('© OpenStreetMap contributors', false),
      }),
  },
];

export const DEFAULT_BASEMAP_ID: BasemapId = 'cesium-world';

/**
 * Swap the Viewer's base imagery to the requested basemap. Removes all
 * existing imagery layers and inserts the new one. Safe to call on every
 * selection change — does nothing if the viewer has been destroyed.
 *
 * Terrain, entities, and camera state are untouched.
 */
export async function applyBasemapToViewer(
  viewer: CesiumViewer,
  basemapId: BasemapId,
): Promise<void> {
  if (!viewer || viewer.isDestroyed()) return;
  const def = BASEMAPS.find((b) => b.id === basemapId) ?? BASEMAPS[0];
  try {
    const result = await def.make();
    if (viewer.isDestroyed()) return;
    viewer.imageryLayers.removeAll();
    if (result instanceof ImageryLayer) {
      viewer.imageryLayers.add(result);
    } else {
      // UrlTemplateImageryProvider or IonImageryProvider
      viewer.imageryLayers.addImageryProvider(result);
    }
  } catch (err) {
    console.warn('[basemaps] imagery swap failed:', err);
  }
}

interface BasemapPickerProps {
  value: BasemapId;
  onChange: (id: BasemapId) => void;
  className?: string;
  /** Label shown before the dropdown. Omit for icon-only. */
  label?: string;
}

/**
 * Compact dropdown rendering the five basemaps. Styled to sit on top of
 * a Cesium viewer (dark translucent background + accent focus ring).
 */
export function BasemapPicker({
  value,
  onChange,
  className,
  label,
}: BasemapPickerProps) {
  return (
    <label
      className={`inline-flex items-center gap-2 text-[11px] text-gray-300 ${className ?? ''}`}
    >
      {label && <span className="text-gray-400">{label}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as BasemapId)}
        className="bg-navy-900/85 border border-navy-700 rounded px-2 py-1 text-white backdrop-blur focus:outline-none focus:ring-1 focus:ring-accent/60"
        aria-label="Basemap"
      >
        {BASEMAPS.map((b) => (
          <option key={b.id} value={b.id}>
            {b.label}
          </option>
        ))}
      </select>
    </label>
  );
}
